import type { FleetMissionType, GalaxyPosition, PlayerRace, ShipId } from '@kaeliux/shared';
import { getSystemStarImageIndex } from '@kaeliux/game-engine';
import { getShipIcon } from '../utils/shipIcons';
import { raceAccentStyle } from '../utils/raceColors';
import { ProgressBar } from './ui/ProgressBar';

import asteroid1 from '../assets/images/galaxy/asteroid1.png';
import asteroid2 from '../assets/images/galaxy/asteroid2.png';
import asteroid3 from '../assets/images/galaxy/asteroid3.png';
import star1 from '../assets/images/galaxy/star1.png';
import star2 from '../assets/images/galaxy/star2.png';
import star3 from '../assets/images/galaxy/star3.png';
import star4 from '../assets/images/galaxy/star4.png';
import star5 from '../assets/images/galaxy/star5.png';
import star6 from '../assets/images/galaxy/star6.png';
import star7 from '../assets/images/galaxy/star7.png';
import star8 from '../assets/images/galaxy/star8.png';

const ASTEROID_IMAGES = [asteroid1, asteroid2, asteroid3];
const STAR_IMAGES = [star1, star2, star3, star4, star5, star6, star7, star8];

function planetImageUrl(planetType: number): string {
    return new URL(`../assets/images/planets/planet_${planetType}.png`, import.meta.url).href;
}

export function targetImage(mission: FleetMissionType, target: GalaxyPosition): string {
    if (mission === 'harvestAsteroid') {
        const idx = ((target.galaxy * 7 + target.system * 3 + target.position) % ASTEROID_IMAGES.length);
        return ASTEROID_IMAGES[idx]!;
    }
    if (mission === 'sunAttack' || mission === 'sunDefence' || mission === 'sunGarrison' || target.position === 0) {
        const starIdx = getSystemStarImageIndex(target.galaxy, target.system) - 1;
        return STAR_IMAGES[starIdx] ?? star1;
    }
    const planetIdx = ((target.galaxy * 11 + target.system * 5 + target.position) % 18) + 1;
    return planetImageUrl(planetIdx);
}

interface FleetProgressTrackerProps {
    progress: number;
    outbound: boolean;
    race: PlayerRace;
    originPlanetType: number;
    mission: FleetMissionType;
    target: GalaxyPosition;
    leadShipId: ShipId;
}

export function FleetProgressTracker({
    progress,
    outbound,
    race,
    originPlanetType,
    mission,
    target,
    leadShipId,
}: FleetProgressTrackerProps) {
    const raw = Math.min(1, Math.max(0, progress));
    const clamped = Number.isFinite(raw) ? raw : 0;

    const originImg = planetImageUrl(originPlanetType);
    const destImg = targetImage(mission, target);

    const leftImg = outbound ? originImg : destImg;
    const rightImg = outbound ? destImg : originImg;
    const shipFlipped = true;

    const trackPercent = 8 + clamped * 84;

    return (
        <div className="flex items-center gap-2 mt-1.5" data-testid="fleet-progress-tracker" style={raceAccentStyle(race)}>
            <img
                src={leftImg}
                alt=""
                className="w-6 h-6 rounded-full shrink-0 object-cover border border-hairline"
                data-testid="progress-origin"
            />
            <div className="flex-1 relative h-5">
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2">
                    <ProgressBar value={progress} accent="race" />
                </div>
                <img
                    src={getShipIcon(race, leadShipId)}
                    alt=""
                    className="absolute top-1/2 -translate-y-1/2 w-5 h-5 drop-shadow-sprite transition-[left] duration-300"
                    data-testid="progress-ship"
                    style={{
                        left: `${trackPercent}%`,
                        transform: `translateY(-50%)${shipFlipped ? ' scaleX(-1)' : ''}`,
                    }}
                />
            </div>
            <img
                src={rightImg}
                alt=""
                className="w-6 h-6 rounded-full shrink-0 object-cover border border-hairline"
                data-testid="progress-destination"
            />
        </div>
    );
}
